<template>
  <div class="brands-page">
    <div class="page-container">
      <div class="page-header">
        <h1 class="page-title">Shop by Brand</h1>
        <p class="page-subtitle">Discover quality products from your favorite brands</p>
      </div>

      <!-- Search Bar -->
      <div class="search-wrapper">
        <div class="search-box">
          <i class="fas fa-search"></i>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search brands..."
            @input="filterBrands"
          />
        </div>
      </div>

      <!-- Featured Brands Section -->
      <div v-if="featuredBrands.length > 0" class="brands-section">
        <h2 class="section-title">Featured Brands</h2>
        <div class="brands-grid">
          <a
            v-for="brand in featuredBrands"
            :key="'featured-' + brand.id"
            :href="`/brands/${brand.slug}`"
            class="brand-card featured"
          >
            <div class="brand-logo">
              <img v-if="brand.logo_url" :src="brand.logo_url" :alt="brand.name" />
              <div v-else class="brand-initial">{{ brand.name.charAt(0) }}</div>
            </div>
            <div class="brand-info">
              <h3>{{ brand.name }}</h3>
              <p class="brand-stats">{{ brand.active_products_count || 0 }} products</p>
            </div>
            <i class="fas fa-chevron-right arrow"></i>
          </a>
        </div>
      </div>

      <!-- All Brands Section -->
      <div class="brands-section">
        <h2 v-if="featuredBrands.length > 0" class="section-title">All Brands</h2>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          <p>Loading brands...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredBrands.length === 0" class="empty-state">
          <i class="fas fa-store-slash"></i>
          <h2>No Brands Found</h2>
          <p>Try a different search term</p>
        </div>

        <!-- Brands Grid -->
        <div v-else class="brands-grid">
          <a
            v-for="brand in filteredBrands"
            :key="brand.id"
            :href="`/brands/${brand.slug}`"
            class="brand-card"
          >
            <div class="brand-logo">
              <img v-if="brand.logo_url" :src="brand.logo_url" :alt="brand.name" />
              <div v-else class="brand-initial">{{ brand.name.charAt(0) }}</div>
            </div>
            <div class="brand-info">
              <h3>{{ brand.name }}</h3>
              <p class="brand-stats">{{ brand.active_products_count || 0 }} products</p>
            </div>
            <i class="fas fa-chevron-right arrow"></i>
          </a>
        </div>

        <!-- Pagination -->
        <div v-if="brands.last_page > 1" class="pagination-wrapper">
          <a
            v-if="brands.prev_page_url"
            :href="brands.prev_page_url"
            class="pagination-btn"
          >
            <i class="fas fa-chevron-left"></i> Previous
          </a>

          <span class="page-info">
            Page {{ brands.current_page }} of {{ brands.last_page }}
          </span>

          <a
            v-if="brands.next_page_url"
            :href="brands.next_page_url"
            class="pagination-btn"
          >
            Next <i class="fas fa-chevron-right"></i>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BrandList',
  props: {
    brands: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      searchQuery: '',
      loading: false,
      filteredBrands: []
    }
  },
  computed: {
    featuredBrands() {
      return this.brands.data ? this.brands.data.filter(b => b.is_featured) : []
    },
    allBrands() {
      return this.brands.data || []
    }
  },
  mounted() {
    this.filteredBrands = this.allBrands
  },
  methods: {
    filterBrands() {
      if (!this.searchQuery.trim()) {
        this.filteredBrands = this.allBrands
        return
      }

      const query = this.searchQuery.toLowerCase()
      this.filteredBrands = this.allBrands.filter(brand =>
        brand.name.toLowerCase().includes(query)
      )
    }
  },
  watch: {
    brands: {
      handler() {
        this.filteredBrands = this.allBrands
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.brands-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 3rem;
}

.page-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
}

.page-header {
  text-align: center;
  padding: 1.5rem 1rem;
  background: #13322C;
  margin: 0 -1rem 1.5rem;
  border-radius: 0 0 12px 12px;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 0.4rem;
}

.page-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9rem;
  margin: 0;
}

.search-wrapper {
  max-width: 450px;
  margin: 0 auto 1.5rem;
}

.search-box {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 0 1rem;
}

.search-box:focus-within {
  border-color: #FD6600;
  box-shadow: 0 0 0 3px rgba(253, 102, 0, 0.1);
}

.search-box i {
  color: #666;
  margin-right: 0.75rem;
  font-size: 0.9rem;
}

.search-box input {
  flex: 1;
  border: none;
  padding: 0.75rem 0;
  font-size: 0.95rem;
  outline: none;
  background: transparent;
}

.brands-section {
  margin-bottom: 2rem;
}

.brands-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #313131;
  margin-bottom: 1rem;
  padding-left: 0.75rem;
  border-left: 3px solid #FD6600;
}

.brands-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
}

.brand-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  padding: 0.875rem 1rem;
  text-decoration: none;
  color: inherit;
  transition: all 0.2s ease;
}

.brand-card:hover {
  border-color: #FD6600;
  box-shadow: 0 4px 12px rgba(253, 102, 0, 0.1);
  transform: translateY(-2px);
}

.brand-card.featured {
  border-color: #FD6600;
  background: linear-gradient(135deg, #fff8f5 0%, #fff 100%);
}

.brand-logo {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f8f8;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 4px;
}

.brand-initial {
  font-size: 1.25rem;
  font-weight: 700;
  color: #FD6600;
}

.brand-info {
  flex: 1;
  min-width: 0;
}

.brand-info h3 {
  font-size: 0.95rem;
  font-weight: 600;
  color: #313131;
  margin: 0 0 0.2rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.brand-stats {
  font-size: 0.8rem;
  color: #666;
  margin: 0;
}

.arrow {
  color: #ccc;
  font-size: 0.75rem;
  transition: color 0.2s, transform 0.2s;
}

.brand-card:hover .arrow {
  color: #FD6600;
  transform: translateX(3px);
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 2.5rem 1rem;
  color: #666;
}

.loading-state i,
.empty-state i {
  font-size: 2rem;
  color: #FD6600;
  margin-bottom: 0.75rem;
  display: block;
}

.empty-state h2 {
  font-size: 1rem;
  color: #313131;
  margin-bottom: 0.4rem;
}

.pagination-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #313131;
  text-decoration: none;
  font-size: 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.pagination-btn:hover {
  background: #FD6600;
  border-color: #FD6600;
  color: #fff;
}

.page-info {
  font-size: 0.85rem;
  color: #666;
}

/* Mobile */
@media (max-width: 768px) {
  .page-container {
    padding: 0 0.75rem;
  }

  .page-header {
    padding: 1rem 0.75rem;
    margin: 0 -0.75rem 1rem;
    border-radius: 0 0 10px 10px;
  }

  .page-title {
    font-size: 1.2rem;
  }

  .page-subtitle {
    font-size: 0.8rem;
  }

  .search-wrapper {
    margin-bottom: 1rem;
  }

  .search-box {
    padding: 0 0.75rem;
  }

  .search-box i {
    font-size: 0.8rem;
  }

  .search-box input {
    padding: 0.6rem 0;
    font-size: 0.85rem;
  }

  .section-title {
    font-size: 0.9rem;
  }

  .brands-grid {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .brand-card {
    padding: 0.65rem 0.75rem;
  }

  .brand-logo {
    width: 40px;
    height: 40px;
  }

  .brand-initial {
    font-size: 1rem;
  }

  .brand-info h3 {
    font-size: 0.85rem;
  }

  .brand-stats {
    font-size: 0.7rem;
  }

  .arrow {
    font-size: 0.65rem;
  }

  .pagination-btn {
    font-size: 0.75rem;
    padding: 0.4rem 0.75rem;
  }

  .page-info {
    font-size: 0.75rem;
  }
}

/* Small Mobile */
@media (max-width: 480px) {
  .page-title {
    font-size: 1.1rem;
  }

  .page-subtitle {
    font-size: 0.75rem;
  }

  .brand-logo {
    width: 36px;
    height: 36px;
  }

  .brand-initial {
    font-size: 0.9rem;
  }

  .brand-info h3 {
    font-size: 0.8rem;
  }

  .brand-stats {
    font-size: 0.65rem;
  }
}
</style>
